import React, { useState } from 'react';
import { Icons } from './Icons';
import { Typewriter } from './Typewriter';
import { LegalModal } from './LegalModal';
import * as GeminiService from '../services/geminiService';

export const LandingPage = ({ onEnter }: { onEnter: (apiKey: string) => void }) => {
  const [activeLegal, setActiveLegal] = useState<'privacy' | 'tos' | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Auth Modal State
  const [showAuthChoice, setShowAuthChoice] = useState(false);
  const [inputKey, setInputKey] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);

  const handleVerify = async () => {
    if (!inputKey.trim()) {
      setVerifyError("Please enter an API key.");
      return;
    }
    
    setIsVerifying(true);
    setVerifyError(null);
    
    try {
      const isValid = await GeminiService.validateApiKey(inputKey.trim());
      if (isValid) {
        onEnter(inputKey.trim());
      } else {
        setVerifyError("Invalid API Key. Please check the key and try again.");
      }
    } catch (e) {
      setVerifyError("Connection failed. Please check your internet and try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const privacyContent = (
    <>
      <p><strong>Last Updated: October 2023</strong></p>
      <p>Your privacy is our paramount concern. BookWiz is designed with a "Local-First" architecture.</p>
      <h4 className="font-bold text-ink mt-4">1. Data Storage</h4>
      <p>We do not operate a backend database for your stories. All your manuscripts, outlines, and character sheets are stored locally in your browser's LocalStorage.</p>
      <h4 className="font-bold text-ink mt-4">2. AI Processing</h4>
      <p>Your content is transmitted directly to Google's Gemini API for processing and is not stored by us.</p>
    </>
  );

  const tosContent = (
    <>
      <p><strong>Last Updated: October 2023</strong></p>
      <p>By using BookWiz, you agree to the following terms:</p>
      <h4 className="font-bold text-ink mt-4">1. Usage</h4>
      <p>You agree not to use BookWiz to generate illegal or harmful content.</p>
    </>
  );

  return (
    <div className="min-h-screen bg-paper flex flex-col font-sans overflow-x-hidden selection:bg-amber-100 selection:text-amber-900">
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
      
      {/* Dynamic Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 bg-paper">
         <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob"></div>
         <div className="absolute top-0 right-1/4 w-96 h-96 bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>
         <div className="absolute -bottom-8 left-1/3 w-96 h-96 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-4000"></div>
      </div>

      {/* Navbar */}
      <nav className="relative z-50 px-6 py-6 max-w-7xl mx-auto w-full flex justify-between items-center">
        <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setShowAuthChoice(true)}>
           <div className="w-10 h-10 bg-gradient-to-br from-ink to-gray-800 text-white rounded-xl shadow-lg flex items-center justify-center transform group-hover:rotate-3 transition-transform duration-300">
             <Icons.Book />
           </div>
           <span className="text-2xl font-serif font-bold text-ink tracking-tight">BookWiz</span>
        </div>
        
        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-6">
           <button onClick={() => setActiveLegal('privacy')} className="text-sm font-medium text-gray-600 hover:text-ink transition-colors">Privacy</button>
           <button onClick={() => setActiveLegal('tos')} className="text-sm font-medium text-gray-600 hover:text-ink transition-colors">Terms</button>
           <a href="https://github.com/muneebwanee/BookWiz" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-gray-300 hover:shadow-sm transition-all">
             <Icons.Github /> Star on GitHub
           </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button className="md:hidden text-gray-700" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <Icons.X /> : <Icons.Menu />}
        </button>
      </nav>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden relative z-40 bg-white/95 backdrop-blur border-b border-gray-100 p-6 flex flex-col gap-4 animate-slide-down">
           <button onClick={() => setActiveLegal('privacy')} className="text-left py-2 font-medium text-gray-700">Privacy Policy</button>
           <button onClick={() => setActiveLegal('tos')} className="text-left py-2 font-medium text-gray-700">Terms of Service</button>
           <a href="https://github.com/muneebwanee/BookWiz" target="_blank" className="flex items-center gap-2 py-2 font-medium text-gray-700">
             <Icons.Github /> View on GitHub
           </a>
        </div>
      )}

      {/* Hero Section */}
      <main className="relative z-10 flex-1 flex flex-col justify-center">
        <div className="max-w-7xl mx-auto px-6 pt-12 pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Copy & CTA */}
          <div className="text-left space-y-8 animate-fade-in-up">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif font-bold text-ink leading-[1.1] tracking-tight">
              Craft Your <br/>
              <Typewriter words={['Next Best Seller.', 'Magnum Opus.', 'Dream Novel.', 'Legacy.']} />
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 max-w-lg leading-relaxed font-light">
              The professional AI co-authoring suite. Structure your narrative, breathe life into characters, and draft immersive prose without losing your unique voice.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 w-full">
              <button 
                onClick={() => setShowAuthChoice(true)}
                className="w-full sm:w-auto group relative px-8 py-4 bg-ink text-white rounded-xl text-lg font-bold overflow-hidden shadow-2xl shadow-gray-400/20 hover:shadow-gray-400/40 transition-all hover:-translate-y-1"
              >
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-gray-800 to-black opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex items-center justify-center gap-3">
                  Start Writing <Icons.Pen />
                </div>
              </button>
              
              <div className="hidden sm:flex items-center gap-4 text-sm font-medium text-gray-500 pl-2 h-14">
                <span className="flex items-center gap-1.5 text-emerald-600">
                  <span className="bg-emerald-100 rounded-full p-0.5"><Icons.Check /></span> Free Tier Supported
                </span>
              </div>
            </div>
          </div>

          {/* Right Column: Visual Mockup */}
          <div className="relative hidden lg:block h-[600px] perspective-1000 group">
             {/* Abstract CSS Representation of the App Interface */}
             <div className="absolute top-10 left-10 w-full h-full bg-white/90 rounded-2xl shadow-2xl border border-white/50 p-6 transform rotate-y-12 group-hover:rotate-y-6 transition-transform duration-700 ease-out flex flex-col backdrop-blur-sm">
                {/* Mock Header */}
                <div className="h-12 border-b border-gray-100 flex items-center justify-between mb-6 px-2">
                   <div className="flex gap-2">
                     <div className="w-3 h-3 rounded-full bg-red-400"></div>
                     <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                     <div className="w-3 h-3 rounded-full bg-green-400"></div>
                   </div>
                   <div className="flex gap-2">
                      <div className="h-2 w-16 bg-gray-100 rounded-full"></div>
                      <div className="h-2 w-8 bg-gray-100 rounded-full"></div>
                   </div>
                </div>
                {/* Mock Content */}
                <div className="flex-1 flex gap-6 overflow-hidden">
                   <div className="w-1/4 h-full bg-gray-50/50 rounded-lg p-4 space-y-3 border border-gray-100/50">
                      <div className="h-2 w-12 bg-gray-200 rounded"></div>
                      <div className="h-8 w-full bg-white border border-gray-200 rounded shadow-sm flex items-center px-2"><div className="w-3/4 h-2 bg-gray-100 rounded"></div></div>
                      <div className="h-8 w-full bg-transparent rounded flex items-center px-2"><div className="w-1/2 h-2 bg-gray-100 rounded"></div></div>
                      <div className="h-8 w-full bg-transparent rounded flex items-center px-2"><div className="w-2/3 h-2 bg-gray-100 rounded"></div></div>
                   </div>
                   <div className="flex-1 space-y-4 pt-2">
                      <div className="h-8 w-3/4 bg-gray-100 rounded animate-pulse"></div>
                      <div className="space-y-2">
                        <div className="h-2 w-full bg-gray-100 rounded"></div>
                        <div className="h-2 w-full bg-gray-100 rounded"></div>
                        <div className="h-2 w-5/6 bg-gray-100 rounded"></div>
                      </div>
                      <div className="space-y-2 pt-4">
                        <div className="h-2 w-full bg-gray-100 rounded"></div>
                        <div className="h-2 w-11/12 bg-gray-100 rounded"></div>
                        <div className="h-2 w-full bg-gray-100 rounded"></div>
                        <div className="h-2 w-4/5 bg-gray-100 rounded"></div>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="bg-white/40 backdrop-blur-sm border-y border-white/50 py-24 relative z-10">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-serif font-bold text-ink mb-4">A complete studio for your imagination</h2>
              <p className="text-gray-500">Powerful tools that respect your creative process.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
               <div className="bg-white/80 p-8 rounded-2xl border border-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                 <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                   <Icons.Book />
                 </div>
                 <h3 className="text-xl font-serif font-bold text-ink mb-3">Deep Structuring</h3>
                 <p className="text-gray-600 leading-relaxed text-sm">
                   Generate industry-standard outlines. Control pacing, plot beats, and chapter arcs with a smart outlining engine.
                 </p>
               </div>
               
               <div className="bg-white/80 p-8 rounded-2xl border border-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                 <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                   <Icons.Users />
                 </div>
                 <h3 className="text-xl font-serif font-bold text-ink mb-3">Living Characters</h3>
                 <p className="text-gray-600 leading-relaxed text-sm">
                   Create expansive casts with complex internal conflicts, distinctive voices, and dynamic developmental arcs.
                 </p>
               </div>
               
               <div className="bg-white/80 p-8 rounded-2xl border border-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                 <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                   <Icons.Sparkles />
                 </div>
                 <h3 className="text-xl font-serif font-bold text-ink mb-3">Immersive Drafting</h3>
                 <p className="text-gray-600 leading-relaxed text-sm">
                   Turn summaries into rich prose. Our AI focuses on "Show, Don't Tell" and sensory details to produce publication-ready drafts.
                 </p>
               </div>
            </div>
          </div>
        </div>

        {/* Tech Stack / Trust */}
        <div className="py-24 text-center relative z-10 px-6">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-12">Security & Technology</p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
             <div className="flex items-center gap-3 px-5 py-3 bg-white/50 rounded-full shadow-sm border border-gray-100">
               <Icons.Google /> <span className="font-bold text-gray-700">Google Cloud</span>
             </div>
             <div className="flex items-center gap-3 px-5 py-3 bg-white/50 rounded-full shadow-sm border border-gray-100">
               <Icons.Gemini /> <span className="font-bold text-gray-700">Gemini Pro</span>
             </div>
             <div className="flex items-center gap-3 px-5 py-3 bg-white/50 rounded-full shadow-sm border border-gray-100">
               <Icons.Github /> <span className="font-bold text-gray-700">Open Source</span>
             </div>
             <div className="flex items-center gap-3 px-5 py-3 bg-white/50 rounded-full shadow-sm border border-gray-100">
               <Icons.Shield /> <span className="font-bold text-gray-700">Local Privacy</span>
             </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
             <div className="w-6 h-6 bg-ink text-white rounded flex items-center justify-center"><Icons.Book /></div>
             <div className="text-sm font-bold text-ink">BookWiz</div>
             <span className="text-gray-300 mx-2">|</span>
             <div className="text-sm text-gray-500">
                © {new Date().getFullYear()} Open Source.
             </div>
          </div>
          <div className="flex gap-4 md:gap-8 text-sm font-medium text-gray-600 flex-wrap justify-center">
             <button onClick={() => setActiveLegal('privacy')} className="hover:text-ink transition-colors">Privacy Policy</button>
             <button onClick={() => setActiveLegal('tos')} className="hover:text-ink transition-colors">Terms of Service</button>
             <a href="https://github.com/muneebwanee/BookWiz" target="_blank" rel="noopener noreferrer" className="hover:text-ink transition-colors">Developed by Muneeb Wani</a>
          </div>
        </div>
      </footer>

      {activeLegal === 'privacy' && <LegalModal title="Privacy Policy" content={privacyContent} onClose={() => setActiveLegal(null)} />}
      {activeLegal === 'tos' && <LegalModal title="Terms of Service" content={tosContent} onClose={() => setActiveLegal(null)} />}
      
      {/* Auth Input Modal */}
      {showAuthChoice && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
           <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl animate-slide-up overflow-hidden max-h-[90vh] flex flex-col">
              <div className="p-8 pb-4 overflow-y-auto">
                 <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-serif font-bold text-ink">Enter API Key</h3>
                    <button onClick={() => setShowAuthChoice(false)} className="text-gray-400 hover:text-gray-600"><Icons.X /></button>
                 </div>

                 <div className="space-y-6">
                    <p className="text-gray-600 text-sm">
                      Enter your Google Gemini API Key to start. Your key is stored locally in your browser and used only to communicate with Google.
                    </p>

                    <div>
                      <input 
                        type="password" 
                        placeholder="Paste your API Key here (starts with AIza...)"
                        className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-ink focus:border-ink outline-none bg-gray-50 text-gray-800 font-mono text-sm"
                        value={inputKey}
                        onChange={(e) => {
                          setInputKey(e.target.value);
                          setVerifyError(null);
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                      />
                      {verifyError && (
                        <p className="text-red-500 text-xs mt-2 flex items-center gap-1 font-medium">
                          <Icons.Info className="w-3 h-3" /> {verifyError}
                        </p>
                      )}
                    </div>
                    
                    <button 
                      onClick={handleVerify}
                      disabled={isVerifying || !inputKey}
                      className="w-full py-4 bg-ink text-white rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                    >
                       {isVerifying ? (
                         <>Verifying Key <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /></>
                       ) : (
                         <>Verify & Start Writing <Icons.ChevronDown className="-rotate-90 w-4 h-4" /></>
                       )}
                    </button>

                    {/* Inline Tutorial Toggle */}
                    <div className="pt-2 border-t border-gray-100">
                      <button 
                        onClick={() => setShowTutorial(!showTutorial)}
                        className="w-full flex justify-between items-center text-sm font-medium text-indigo-600 hover:bg-indigo-50 p-3 rounded-lg transition-colors group"
                      >
                         <span>Don't have an API Key?</span>
                         <span className={`transform transition-transform ${showTutorial ? 'rotate-180' : ''}`}><Icons.ChevronDown className="w-4 h-4" /></span>
                      </button>

                      {showTutorial && (
                        <div className="mt-3 bg-gray-50 p-4 rounded-xl text-sm text-gray-700 space-y-4 animate-fade-in-up border border-gray-100">
                           <div>
                              <h5 className="font-bold text-ink mb-1">Option 1: Free Key (Recommended for testing)</h5>
                              <ol className="list-decimal pl-4 space-y-1 text-xs text-gray-600">
                                <li>Visit <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline hover:text-indigo-800">Google AI Studio</a>.</li>
                                <li>Click <strong>Create API Key</strong>.</li>
                                <li>Copy the key and paste it above.</li>
                              </ol>
                           </div>
                           <div className="border-t border-gray-200 pt-3">
                              <h5 className="font-bold text-ink mb-1">Option 2: Paid Key (For heavy usage)</h5>
                              <ol className="list-decimal pl-4 space-y-1 text-xs text-gray-600">
                                <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline hover:text-indigo-800">Google Cloud Console</a>.</li>
                                <li>Create a project & enable billing.</li>
                                <li>Enable <strong>Gemini API</strong> and create a key.</li>
                              </ol>
                           </div>
                        </div>
                      )}
                    </div>
                 </div>
              </div>

              <div className="bg-gray-50 p-4 text-center border-t border-gray-100 text-xs text-gray-500">
                  By clicking verify, you agree to our Terms of Service.
              </div>
           </div>
        </div>
      )}
    </div>
  );
};